// @ts-nocheck
/**
 * Multi-Tenant Management Component
 * Provides district-level overview and school-level management interface
 * Includes user management with bulk operations and role-based access control
 */

import React, { useState, useEffect } from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableContainer,
  Button,
  Modal,
  Form,
  FormGroup,
  TextInput,
  Dropdown,
  NumberInput,
  Toggle,
  Tag,
  InlineNotification,
  Grid,
  Column,
  Tile,
  Stack,
  Heading,
  Loading,
  OverflowMenu,
  OverflowMenuItem,
  Pagination,
  Search,
  FileUploader,
  ProgressBar,
  Checkbox,
} from '@carbon/react';
import {
  Add,
  Edit,
  TrashCan,
  View,
  Download,
  Upload,
  UserMultiple,
  Education,
  Enterprise,
  CheckmarkFilled,
  Warning,
  Error as ErrorIcon,
  Renew,
  Settings,
} from '@carbon/icons-react';

interface District {
  district_id: string;
  name: string;
  code: string;
  state: string;
  total_schools: number;
  total_students: number;
  total_teachers: number;
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  sis_integration: boolean;
  compliance_status: 'compliant' | 'warning' | 'critical';
}

interface School {
  school_id: string;
  district_id: string;
  name: string;
  code: string;
  address: string;
  principal_name: string;
  total_students: number;
  total_teachers: number;
  grade_levels: string[];
  status: 'active' | 'inactive';
  created_at: string;
}

interface AdminUser {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'district_admin' | 'school_admin' | 'teacher' | 'parent' | 'student';
  district_id?: string;
  school_id?: string;
  status: 'active' | 'inactive' | 'pending';
  last_login: string;
  permissions: string[];
}

interface BulkOperation {
  operation_id: string;
  type: 'import' | 'export' | 'update' | 'delete';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_records: number;
  processed_records: number;
  failed_records: number;
  created_at: string;
  completed_at?: string;
  error_log?: string;
}

const MultiTenantManagement: React.FC = () => {
  const [activeView, setActiveView] = useState<'districts' | 'schools' | 'users' | 'bulk-ops'>('districts');
  const [districts, setDistricts] = useState<District[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [bulkOperations, setBulkOperations] = useState<BulkOperation[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedSchool, setSelectedSchool] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'district' | 'school' | 'user' | 'bulk-import'>('district');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Form states
  const [formData, setFormData] = useState<any>({});
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState('');

  useEffect(() => {
    loadData();
  }, [activeView, selectedDistrict, selectedSchool]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeView) {
        case 'districts':
          await loadDistricts();
          break;
        case 'schools':
          await loadSchools();
          break;
        case 'users':
          await loadUsers();
          break;
        case 'bulk-ops':
          await loadBulkOperations();
          break;
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDistricts = async () => {
    // Mock data - in real implementation, fetch from API
    const mockDistricts: District[] = [
      {
        district_id: 'dist_001',
        name: 'Springfield Unified School District',
        code: 'SUSD',
        state: 'IL',
        total_schools: 15,
        total_students: 8500,
        total_teachers: 420,
        status: 'active',
        created_at: '2024-01-15',
        sis_integration: true,
        compliance_status: 'compliant',
      },
      {
        district_id: 'dist_002',
        name: 'Riverside School District',
        code: 'RSD',
        state: 'CA',
        total_schools: 8,
        total_students: 3200,
        total_teachers: 180,
        status: 'active',
        created_at: '2024-02-20',
        sis_integration: false,
        compliance_status: 'warning',
      },
    ];
    setDistricts(mockDistricts);
  };

  const loadSchools = async () => {
    // Mock data - in real implementation, fetch from API
    const mockSchools: School[] = [
      {
        school_id: 'school_001',
        district_id: 'dist_001',
        name: 'Springfield Elementary',
        code: 'SE',
        address: '123 Main St, Springfield, IL',
        principal_name: 'Dr. Jane Smith',
        total_students: 450,
        total_teachers: 25,
        grade_levels: ['K', '1', '2', '3', '4', '5'],
        status: 'active',
        created_at: '2024-01-20',
      },
      {
        school_id: 'school_002',
        district_id: 'dist_001',
        name: 'Springfield Middle School',
        code: 'SMS',
        address: '456 Oak Ave, Springfield, IL',
        principal_name: 'Mr. John Johnson',
        total_students: 380,
        total_teachers: 22,
        grade_levels: ['6', '7', '8'],
        status: 'active',
        created_at: '2024-01-22',
      },
    ];
    setSchools(selectedDistrict ? mockSchools.filter(s => s.district_id === selectedDistrict) : mockSchools);
  };

  const loadUsers = async () => {
    // Mock data - in real implementation, fetch from API
    const mockUsers: AdminUser[] = [
      {
        user_id: 'user_001',
        email: 'admin@springfield.edu',
        first_name: 'Sarah',
        last_name: 'Wilson',
        role: 'district_admin',
        district_id: 'dist_001',
        status: 'active',
        last_login: '2024-12-15T10:30:00Z',
        permissions: ['manage_users', 'view_analytics', 'manage_content'],
      },
      {
        user_id: 'user_002',
        email: 'principal@se.springfield.edu',
        first_name: 'Jane',
        last_name: 'Smith',
        role: 'school_admin',
        district_id: 'dist_001',
        school_id: 'school_001',
        status: 'active',
        last_login: '2024-12-14T14:15:00Z',
        permissions: ['manage_school_users', 'view_school_analytics'],
      },
    ];
    setUsers(mockUsers);
  };

  const loadBulkOperations = async () => {
    // Mock data - in real implementation, fetch from API
    const mockOperations: BulkOperation[] = [
      {
        operation_id: 'bulk_001',
        type: 'import',
        status: 'completed',
        total_records: 150,
        processed_records: 147,
        failed_records: 3,
        created_at: '2024-12-10T09:00:00Z',
        completed_at: '2024-12-10T09:15:00Z',
      },
      {
        operation_id: 'bulk_002',
        type: 'export',
        status: 'processing',
        total_records: 500,
        processed_records: 320,
        failed_records: 0,
        created_at: '2024-12-15T11:30:00Z',
      },
    ];
    setBulkOperations(mockOperations);
  };

  const handleCreateDistrict = () => {
    setModalType('district');
    setFormData({});
    setIsModalOpen(true);
  };

  const handleCreateSchool = () => {
    setModalType('school');
    setFormData({});
    setIsModalOpen(true);
  };

  const handleCreateUser = () => {
    setModalType('user');
    setFormData({});
    setIsModalOpen(true);
  };

  const handleBulkImport = () => {
    setModalType('bulk-import');
    setFormData({});
    setIsModalOpen(true);
  };

  const handleSubmitForm = async () => {
    try {
      // In real implementation, call appropriate API endpoint
      setIsModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Failed to submit form:', error);
    }
  };

  const handleBulkUserAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;

    try {
      // In real implementation, call bulk action API
      setSelectedUsers([]);
      setBulkAction('');
      await loadUsers();
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate file upload progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploadProgress(i);
      }

      // In real implementation, handle file upload to API
      setIsModalOpen(false);
      await loadBulkOperations();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'compliant':
      case 'completed':
        return 'green';
      case 'warning':
      case 'processing':
        return 'yellow';
      case 'inactive':
      case 'critical':
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'compliant':
      case 'completed':
        return <CheckmarkFilled style={{ color: '#24a148' }} />;
      case 'warning':
      case 'processing':
        return <Warning style={{ color: '#f1c21b' }} />;
      case 'inactive':
      case 'critical':
      case 'failed':
        return <ErrorIcon style={{ color: '#fa4d56' }} />;
      default:
        return null;
    }
  };

  // District table configuration
  const districtHeaders = [
    { key: 'name', header: 'District Name' },
    { key: 'code', header: 'Code' },
    { key: 'state', header: 'State' },
    { key: 'schools', header: 'Schools' },
    { key: 'students', header: 'Students' },
    { key: 'teachers', header: 'Teachers' },
    { key: 'status', header: 'Status' },
    { key: 'compliance', header: 'Compliance' },
    { key: 'actions', header: 'Actions' },
  ];

  const districtRows = districts.map(district => ({
    id: district.district_id,
    name: (
      <Stack gap={1}>
        <Text style={{ fontWeight: 'bold' }}>{district.name}</Text>
        <Text size="sm" style={{ color: '#6f6f6f' }}>
          Created {new Date(district.created_at).toLocaleDateString()}
        </Text>
      </Stack>
    ),
    code: district.code,
    state: district.state,
    schools: district.total_schools.toLocaleString(),
    students: district.total_students.toLocaleString(),
    teachers: district.total_teachers.toLocaleString(),
    status: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {getStatusIcon(district.status)}
        <Tag type={getStatusColor(district.status)} size="sm">
          {district.status}
        </Tag>
      </div>
    ),
    compliance: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {getStatusIcon(district.compliance_status)}
        <Tag type={getStatusColor(district.compliance_status)} size="sm">
          {district.compliance_status}
        </Tag>
      </div>
    ),
    actions: (
      <OverflowMenu flipped iconDescription="Actions">
        <OverflowMenuItem itemText="View Details" onClick={() => setSelectedDistrict(district.district_id)} />
        <OverflowMenuItem itemText="Edit District" onClick={() => {}} />
        <OverflowMenuItem itemText="Sync SIS Data" onClick={() => {}} />
        <OverflowMenuItem itemText="Generate Report" onClick={() => {}} />
      </OverflowMenu>
    ),
  }));

  // School table configuration
  const schoolHeaders = [
    { key: 'name', header: 'School Name' },
    { key: 'code', header: 'Code' },
    { key: 'principal', header: 'Principal' },
    { key: 'students', header: 'Students' },
    { key: 'teachers', header: 'Teachers' },
    { key: 'grades', header: 'Grade Levels' },
    { key: 'status', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const schoolRows = schools.map(school => ({
    id: school.school_id,
    name: (
      <Stack gap={1}>
        <Text style={{ fontWeight: 'bold' }}>{school.name}</Text>
        <Text size="sm" style={{ color: '#6f6f6f' }}>
          {school.address}
        </Text>
      </Stack>
    ),
    code: school.code,
    principal: school.principal_name,
    students: school.total_students.toLocaleString(),
    teachers: school.total_teachers.toLocaleString(),
    grades: school.grade_levels.join(', '),
    status: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {getStatusIcon(school.status)}
        <Tag type={getStatusColor(school.status)} size="sm">
          {school.status}
        </Tag>
      </div>
    ),
    actions: (
      <OverflowMenu flipped iconDescription="Actions">
        <OverflowMenuItem itemText="View Details" onClick={() => setSelectedSchool(school.school_id)} />
        <OverflowMenuItem itemText="Edit School" onClick={() => {}} />
        <OverflowMenuItem itemText="Manage Users" onClick={() => {}} />
        <OverflowMenuItem itemText="Generate Report" onClick={() => {}} />
      </OverflowMenu>
    ),
  }));

  // User table configuration
  const userHeaders = [
    { key: 'select', header: '' },
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'role', header: 'Role' },
    { key: 'organization', header: 'Organization' },
    { key: 'status', header: 'Status' },
    { key: 'lastLogin', header: 'Last Login' },
    { key: 'actions', header: 'Actions' },
  ];

  const userRows = users.map(user => ({
    id: user.user_id,
    select: (
      <Checkbox
        id={`user-${user.user_id}`}
        checked={selectedUsers.includes(user.user_id)}
        onChange={checked => {
          if (checked) {
            setSelectedUsers([...selectedUsers, user.user_id]);
          } else {
            setSelectedUsers(selectedUsers.filter(id => id !== user.user_id));
          }
        }}
      />
    ),
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    role: (
      <Tag type="outline" size="sm">
        {user.role.replace('_', ' ').toUpperCase()}
      </Tag>
    ),
    organization: user.school_id ? 'School Level' : user.district_id ? 'District Level' : 'Platform Level',
    status: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {getStatusIcon(user.status)}
        <Tag type={getStatusColor(user.status)} size="sm">
          {user.status}
        </Tag>
      </div>
    ),
    lastLogin: user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never',
    actions: (
      <OverflowMenu flipped iconDescription="Actions">
        <OverflowMenuItem itemText="View Profile" onClick={() => {}} />
        <OverflowMenuItem itemText="Edit User" onClick={() => {}} />
        <OverflowMenuItem itemText="Reset Password" onClick={() => {}} />
        <OverflowMenuItem itemText="View Permissions" onClick={() => {}} />
      </OverflowMenu>
    ),
  }));

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Loading description="Loading multi-tenant data..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #e0e0e0' }}>
        <Button
          kind={activeView === 'districts' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('districts')}
          renderIcon={Enterprise}
        >
          Districts
        </Button>
        <Button
          kind={activeView === 'schools' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('schools')}
          renderIcon={Education}
        >
          Schools
        </Button>
        <Button
          kind={activeView === 'users' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('users')}
          renderIcon={UserMultiple}
        >
          Users
        </Button>
        <Button
          kind={activeView === 'bulk-ops' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('bulk-ops')}
          renderIcon={Renew}
        >
          Bulk Operations
        </Button>
      </div>

      {/* Action Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Search
            placeholder={`Search ${activeView}...`}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="sm"
          />
          {selectedDistrict && activeView === 'schools' && (
            <Tag type="blue" size="sm">
              District: {districts.find(d => d.district_id === selectedDistrict)?.name}
            </Tag>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {activeView === 'districts' && (
            <Button kind="primary" size="sm" renderIcon={Add} onClick={handleCreateDistrict}>
              Add District
            </Button>
          )}
          {activeView === 'schools' && (
            <Button kind="primary" size="sm" renderIcon={Add} onClick={handleCreateSchool}>
              Add School
            </Button>
          )}
          {activeView === 'users' && (
            <>
              <Button kind="tertiary" size="sm" renderIcon={Upload} onClick={handleBulkImport}>
                Bulk Import
              </Button>
              <Button kind="primary" size="sm" renderIcon={Add} onClick={handleCreateUser}>
                Add User
              </Button>
            </>
          )}
          {activeView === 'bulk-ops' && (
            <Button kind="primary" size="sm" renderIcon={Download} onClick={() => {}}>
              Export Data
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Actions for Users */}
      {activeView === 'users' && selectedUsers.length > 0 && (
        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f4f4f4', borderRadius: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Text>{selectedUsers.length} users selected</Text>
            <Dropdown
              id="bulk-action-select"
              label="Choose action..."
              items={[
                { id: 'activate', text: 'Activate Users' },
                { id: 'deactivate', text: 'Deactivate Users' },
                { id: 'reset-password', text: 'Reset Passwords' },
                { id: 'export', text: 'Export Selected' },
              ]}
              selectedItem={bulkAction ? { id: bulkAction, text: bulkAction } : undefined}
              onChange={({ selectedItem }) => setBulkAction(selectedItem?.id || '')}
              size="sm"
            />
            <Button size="sm" onClick={handleBulkUserAction} disabled={!bulkAction}>
              Apply Action
            </Button>
            <Button kind="ghost" size="sm" onClick={() => setSelectedUsers([])}>
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      {activeView === 'districts' && (
        <DataTable rows={districtRows} headers={districtHeaders}>
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <TableContainer title="District Management">
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map(cell => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}

      {activeView === 'schools' && (
        <DataTable rows={schoolRows} headers={schoolHeaders}>
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <TableContainer title="School Management">
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map(cell => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}

      {activeView === 'users' && (
        <DataTable rows={userRows} headers={userHeaders}>
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <TableContainer title="User Management">
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map(cell => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}

      {activeView === 'bulk-ops' && (
        <div>
          <Heading size="md" style={{ marginBottom: '1rem' }}>
            Bulk Operations History
          </Heading>
          {bulkOperations.map(op => (
            <Tile key={op.operation_id} style={{ marginBottom: '1rem' }}>
              <Grid>
                <Column lg={3}>
                  <Stack gap={2}>
                    <Text style={{ fontWeight: 'bold' }}>{op.type.toUpperCase()} Operation</Text>
                    <Text size="sm">ID: {op.operation_id}</Text>
                  </Stack>
                </Column>
                <Column lg={3}>
                  <Stack gap={2}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {getStatusIcon(op.status)}
                      <Tag type={getStatusColor(op.status)} size="sm">
                        {op.status}
                      </Tag>
                    </div>
                    <Text size="sm">Started: {new Date(op.created_at).toLocaleString()}</Text>
                  </Stack>
                </Column>
                <Column lg={3}>
                  <Stack gap={2}>
                    <Text>
                      Progress: {op.processed_records}/{op.total_records}
                    </Text>
                    {op.status === 'processing' && (
                      <ProgressBar
                        value={(op.processed_records / op.total_records) * 100}
                        label="Processing..."
                        size="small"
                      />
                    )}
                  </Stack>
                </Column>
                <Column lg={3}>
                  <Stack gap={2}>
                    {op.failed_records > 0 && <Text style={{ color: '#fa4d56' }}>Failed: {op.failed_records}</Text>}
                    {op.completed_at && <Text size="sm">Completed: {new Date(op.completed_at).toLocaleString()}</Text>}
                  </Stack>
                </Column>
              </Grid>
            </Tile>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
        <Pagination
          page={currentPage}
          totalItems={100}
          pageSize={pageSize}
          pageSizes={[10, 20, 50, 100]}
          onChange={({ page, pageSize: newPageSize }) => {
            setCurrentPage(page);
            setPageSize(newPageSize);
          }}
        />
      </div>

      {/* Modal for Create/Edit Operations */}
      <Modal
        open={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        modalHeading={`${modalType === 'bulk-import' ? 'Bulk Import' : 'Create'} ${modalType.charAt(0).toUpperCase() + modalType.slice(1)}`}
        primaryButtonText={isUploading ? 'Uploading...' : modalType === 'bulk-import' ? 'Upload' : 'Create'}
        secondaryButtonText="Cancel"
        onRequestSubmit={modalType === 'bulk-import' ? () => {} : handleSubmitForm}
        onSecondarySubmit={() => setIsModalOpen(false)}
        size="md"
      >
        {modalType === 'district' && (
          <Form>
            <FormGroup legendText="District Information">
              <Grid>
                <Column lg={8}>
                  <TextInput
                    id="district-name"
                    labelText="District Name"
                    placeholder="Enter district name..."
                    value={formData.name || ''}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                  />
                </Column>
                <Column lg={8}>
                  <TextInput
                    id="district-code"
                    labelText="District Code"
                    placeholder="Enter district code..."
                    value={formData.code || ''}
                    onChange={e => setFormData({ ...formData, code: e.target.value })}
                  />
                </Column>
              </Grid>
              <Grid>
                <Column lg={8}>
                  <Dropdown
                    id="state-select"
                    titleText="State"
                    label="Select state..."
                    items={[
                      { id: 'CA', text: 'California' },
                      { id: 'IL', text: 'Illinois' },
                      { id: 'NY', text: 'New York' },
                      { id: 'TX', text: 'Texas' },
                    ]}
                    selectedItem={formData.state ? { id: formData.state, text: formData.state } : undefined}
                    onChange={({ selectedItem }) => setFormData({ ...formData, state: selectedItem?.id })}
                  />
                </Column>
                <Column lg={8}>
                  <Toggle
                    id="sis-integration"
                    labelText="Enable SIS Integration"
                    toggled={formData.sis_integration || false}
                    onToggle={toggled => setFormData({ ...formData, sis_integration: toggled })}
                  />
                </Column>
              </Grid>
            </FormGroup>
          </Form>
        )}

        {modalType === 'bulk-import' && (
          <Form>
            <FormGroup legendText="File Upload">
              <FileUploader
                accept={['.csv', '.xlsx']}
                buttonLabel="Choose file"
                filenameStatus="edit"
                iconDescription="Clear file"
                labelDescription="Upload CSV or Excel file with user data"
                labelTitle="Import File"
                multiple={false}
                onChange={e => {
                  if (e.target.files) {
                    handleFileUpload(e.target.files);
                  }
                }}
              />
              {isUploading && (
                <div style={{ marginTop: '1rem' }}>
                  <ProgressBar value={uploadProgress} label={`Uploading... ${uploadProgress}%`} size="small" />
                </div>
              )}
            </FormGroup>
            <InlineNotification
              kind="info"
              title="Import Instructions"
              subtitle="File should contain columns: email, first_name, last_name, role, district_id, school_id (optional)"
              lowContrast
              hideCloseButton
            />
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default MultiTenantManagement;
